<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref, watchEffect} from 'vue'
import {ListDetailLayout} from '@hantera/design-system'
import {useContext, useDialogs, useLocalization} from '@hantera/portal-app'
import useBackend from '@/composition/useBackend'
import RoleDetail from './RoleDetail.vue'
import CreateRoleModal from './CreateRoleModal.vue'
import {RolesDataProvider} from './data-provider'
import {useIamEventsStore} from '@/stores/iam/iam-events-store'
import type {RolesListParams} from '@/backend/iam.types'

const context = useContext()
const { iam: iamService } = useBackend()
const t = useLocalization('iam.roles')
const dialogs = useDialogs()
const iamEvents = useIamEventsStore()

watchEffect(() => {
  if (context.currentView.value?.name) {
    context.currentView.value.name.value = t('viewTitle')
  }
})

const canRead = computed(() => context.authorize('iam/roles:read'))
const canWrite = computed(() => context.authorize('iam/roles:write'))

const filters = ref<RolesListParams>({
  includeSystem: true,
  limit: 100,
})

const rolesProvider = new RolesDataProvider(filters, iamService, t)

const selectedRoleId = ref<string | null>(null)

function openCreateModal() {
  dialogs.openModal(t('create.title'), CreateRoleModal, true, {
    iamService,
    onCreated: (role: any) => {
      // Refresh list and select new role
      rolesProvider.refresh()
      selectedRoleId.value = role.id
    },
    onCancel: () => {
      // Modal will close automatically
    },
  })
}

let unsubscribe: (() => void) | null = null

onMounted(() => {
  if (!canRead.value) {
    context.currentView.value?.close()
  }

  unsubscribe = iamEvents.subscribeToRolesList(() => {
    rolesProvider.refresh()
  })
})

onUnmounted(() => {
  unsubscribe?.()
})
</script>

<template>
  <div class="roles-view">
    <ListDetailLayout
      :provider="rolesProvider"
      :detail-component="RoleDetail"
      cache-key="iam-roles"
      :selected="selectedRoleId"
      @update:selected="(val: string | null) => (selectedRoleId = val)"
      :allow-multi-select="false"
    >
      <template #list-toolbar>
        <div class="toolbar-content">
          <button v-if="canWrite" @click="openCreateModal" class="btn-primary">
            {{ t('create.button') }}
          </button>
        </div>
      </template>
    </ListDetailLayout>
  </div>
</template>

<style scoped>
.roles-view {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.toolbar-content {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
}
</style>
